import * as React from "react";
import { Dispatch, connect } from "react-redux";
import * as moment from "moment";
import * as _ from 'lodash';
import { Glyphicon, Button, FormControl, Label } from "react-bootstrap";
import { ICluster, IContact, ILogisticsProvider, ICenter, IPosition, IListItem, IMatchHistoryRecipient, ITransportType, IUser } from "@nkr/models/index";
import { MatchHistoryType, ClusterStatus, ChainStatus, DonorType } from "@nkr/enums/index";
import { SwapActionCreators, AppActionCreators, IApplicationState } from "@nkr/store/index";
import { DateProperties } from "../models";
import { RowWrapper, DetailButtonWrapper, FormControlWrapper } from "../styles";
import PositionList from "./positions/position-list";
import DateTimePicker from "@nkr/components/date-time-picker";
import { toast } from "react-toastify";
import { OfferNowType } from "@nkr/enums/swap-offer-now-type";

interface IProps {
    swap: ICluster,
    chainStatusId: ChainStatus,
    clusterStatusId: ClusterStatus,
    collapseStatus: number,
    showDetail: boolean,
    user: IUser,
    offerNowHighlight: boolean,
    onShowMap: (url: string) => void,
    onUpdateAfterReset: () => void,
    onShowActions: (swap: ICluster) => void,
    onChainStatusChange: (swapType: string, chainId: number, chainStatus: number) => void,
    onClusterStatusChange: (clusterId: number, clusterStatus: number) => void,
    onLogisticsProviderChange: (clusterId: number, logisticsProvider: number) => void,
    onSwapManagerChange: (clusterId: number, swapManager: string) => void,
    onLogisticsDateTimeChange: (clusterId: number, logisticsDateTime: string) => void,
    onKickoffDateTimeChange: (clusterId: number, kickoffDateTime: string) => void,
    onMatchHistoryDeadlineChange: (matchHistoryType: MatchHistoryType, clusterId: number, deadlineDateTime: string, list: IMatchHistoryRecipient[]) => void,
    onCommentChange: (clusterId: number, comment: string) => void,
    onShowDetail: (clusterId: number) => void,
    onShowSwapManagement: (chainId: number) => void,
    onUpdateSwaps: (message: string, success: boolean, refresh: boolean) => void
}

interface IStateProps {
    clusterStatusList: IListItem[];
    chainStatusList: IListItem[];
    staffList: IContact[];
    logisticsProviders: ILogisticsProvider[];
    pairStatusList: IListItem[];
    centerList: ICenter[];
    transportTypeList: ITransportType[];
    swapTypeList: IListItem[];
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    refreshSwaps: () => Promise<any>
}

type TProps =
    IProps
    & IStateProps
    & IDispatchProps;

interface IState {
    comment: {
        edit: boolean,
        value: string
    }
}

const initialState: IState = {
    comment: {
        edit: false,
        value: ""
    }
};

class SwapListRow extends React.Component<TProps, IState> {
    readonly state: IState = initialState;

    handleShowDetail = () => {
        this.props.onShowDetail(this.props.swap.id);
    }

    handleChainStatusChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.onChainStatusChange(this.props.swap.swapType, this.props.swap.chainId, parseInt(e.currentTarget.value));
    }

    handleClusterStatusChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        let status = parseInt(e.currentTarget.value);
        let position = this.props.swap.positions.find(x => x.positionNumber == 1);
        let donor = position.donor;

        if (status == ClusterStatus.Stalled && donor.donorType == 'NDD' && this.props.swap.clusterNumber != 1) {
            toast.error(
                <span style={{ fontSize: 18 }}>The cluster starts with a bridge donor and cannot be moved into the STALLED category</span>, {
                autoClose: false,
                className: 'severe-error'
            });
        } else {
            this.props.onClusterStatusChange(this.props.swap.id, status);
        }
    }

    handleSwapManagerChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.onSwapManagerChange(this.props.swap.id, e.currentTarget.value);
    }

    handleLogisticsProviderChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.props.onLogisticsProviderChange(this.props.swap.id, parseInt(e.currentTarget.value));
    }

    handleShowMap = (url: string) => {
        this.props.onShowMap(url);
    }

    handleUpdateAfterReset = () => {
        this.props.onUpdateAfterReset();
    }

    handleShowActions = () => {
        this.props.onShowActions(this.props.swap);
    }

    handleEditComment = () => {
        this.setState({
            comment: { edit: true, value: this.props.swap.note }
        });
    }

    handleCommentChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            comment: { ...this.state.comment, value: e.currentTarget.value }
        });
    }

    handleSaveComment = () => {
        this.setState({
            comment: { ...this.state.comment, edit: false }
        }, () => {
            if (this.state.comment.value != this.props.swap.note)
                this.props.onCommentChange(this.props.swap.id, this.state.comment.value);
        });
    }

    handleCancelComment = (e: React.KeyboardEvent<FormControl>) => {
        if (e.key == "Escape") {
            this.setState({
                comment: { ...this.state.comment, edit: false }
            });
        }
    }

    handleDateTimeValueChange = (property: string, moment: moment.Moment) => {
        switch (property) {
            case DateProperties.DEADLINE_DATETIME:
                var matchHistoryType = this.props.clusterStatusId == ClusterStatus.Offers ? MatchHistoryType.MatchOffer : MatchHistoryType.CrossMatch;
                var clusterId = this.props.swap.id;

                var list = this.props.swap.positions.map(p => {
                    var matchHistoryId = p.matchHistory != null ? p.matchHistory.id : 0;
                    var recipientId = p.recipient.id;

                    return { recipientId, matchHistoryId };
                });

                this.props.onMatchHistoryDeadlineChange(matchHistoryType, clusterId, moment.format("MM/DD/YY HH:mm"), list);

                break;

            case DateProperties.KICKOFF_DATETIME:
                this.props.onKickoffDateTimeChange(this.props.swap.id, moment.format("MM/DD/YY HH:mm"));
                break;

            case DateProperties.LOGISTICS_DATETIME:
                this.props.onLogisticsDateTimeChange(this.props.swap.id, moment.format("MM/DD/YY HH:mm"));
                break;
        }
    }


    renderRange = () => {
        if (this.props.swap.positions.length == 0) return null;

        const position = this.props.swap.positions.find((p: IPosition) => p.positionNumber == 1);
        if (!position) return <span>-</span>;

        let highlightStyle = {};

        if (position.offerNowType == OfferNowType.Yellow) {
            highlightStyle = {
                backgroundColor: "yellow",
                color: "black",
                fontWeight: 700,
                borderRadius: "2px"
            };
        }

        else if (position.offerNowType == OfferNowType.Purple) {

            highlightStyle = {
                backgroundColor: " #adadeb",
                color: "black",
                fontWeight: 700,
                borderRadius: "2px"
            };
        }

        else if (position.offerNowType == OfferNowType.Green) {
            highlightStyle = {
                backgroundColor: "green",
                color: "white",
                fontWeight: 700,
                borderRadius: "2px"
            };
        }

        if (this.props.clusterStatusId != ClusterStatus.Stalled) {

            let highlightStyle = {};


            if (this.props.offerNowHighlight) {
                highlightStyle = {
                    backgroundColor: position.offerNowType == OfferNowType.Purple ? " #adadeb" : "",
                    color: position.offerNowType == OfferNowType.Purple ? "black" : "white",

                    borderRadius: "2px",
                };
            }

            return (
                <span style={highlightStyle}>
                    &nbsp;{position.donationStartDate} - {position.donationEndDate}&nbsp;
                </span>
            );
        } else {
            return (
                <span style={highlightStyle}>
                    &nbsp;{position.donationStartDate} - {position.donationEndDate}&nbsp;
                </span>
            );
        }
    };


    renderClusterDeadline = () => {
        var position = this.props.swap.positions.find(p => p.matchHistory != null);

        if (position != null) {
            if (!position.matchHistory || position.matchHistory.isCancelled != true) {
                return (
                    <DateTimePicker
                        property={DateProperties.DEADLINE_DATETIME}
                        currentValue={position.matchHistory.deadlineDateTime}
                        onValueChanged={this.handleDateTimeValueChange}
                    />
                );
            }
        }
        else
            return null;
    }

    renderNote = () => {
        if (!this.state.comment.edit && this.props.swap.note == "")
            return (
                <div style={{ color: "#ddd" }}>[ click here to add notes... ]</div>
            );

        if (!this.state.comment.edit)
            return (
                <div style={{ width: "100%", whiteSpace: "nowrap", textOverflow: "ellipsis" }} title={this.props.swap.note}>{this.props.swap.note}</div>
            );

        if (this.state.comment.edit)
            return (
                <FormControl
                    value={this.state.comment.value}
                    style={{ height: 30, paddingLeft: 6 }}
                    placeholder="Notes pertaining to this swap..."
                    onChange={this.handleCommentChange}
                    onBlur={this.handleSaveComment}
                    onKeyUp={this.handleCancelComment}
                    autoFocus={true}
                />
            );
    }

    render = () => {
        const swap = this.props.swap;
        const swapManagers = _.sortBy(this.props.staffList.filter((staff: IContact) => staff.isSwapManager && staff.fullName != 'NKR Logistics'), item => item.firstName);
        const courierVendor = (swap.courierVendor != null ? swap.courierVendor : -1);
        const logisticsProvider = this.props.logisticsProviders.find(lp => lp.id == courierVendor);
        const logisticsProviderDescription = (logisticsProvider != null ? logisticsProvider.description : "");

        const glyph = (this.props.showDetail ? "minus" : "plus");
        const showDetail = this.props.showDetail;

        const clusterStatusList = this.props.clusterStatusList.filter(x => x.id != ClusterStatus.KflId);

        return (
            <>
                <RowWrapper className="RowWrapper" showHighlight={this.props.showDetail} useBackupPlan={false}>
                    <td>
                        <DetailButtonWrapper bsSize="xs" onClick={this.handleShowDetail}><Glyphicon glyph={glyph} style={{ padding: 2 }} /></DetailButtonWrapper>
                    </td>

                    <td>{swap.donationType}</td>
                    <td>
                        <span style={{ color: "yellow", cursor: 'default' }} onClick={() => this.props.onShowSwapManagement(swap.chainId)}>{swap.chainId} / {swap.clusterNumber}-{swap.swapType}</span>
                        {this.props.user.email != 'garet.hil@kidneyregistry.org' && (
                            <span style={{ paddingLeft: 10 }}><a href={`/manage-swaps/swap-management?chain=${swap.chainId}`} style={{ color: 'yellow' }} target='_blank'><Glyphicon glyph='cog' /></a></span>
                        )}
                    </td>

                    {this.props.clusterStatusId != ClusterStatus.Stalled && this.props.clusterStatusId != ClusterStatus.Offers && (
                        <td>
                            <FormControlWrapper
                                componentClass="select"
                                value={swap.chainStatusId}
                                style={{ width: "initial" }}
                                onChange={this.handleChainStatusChange}
                            >
                                {this.props.chainStatusList.map((status, index) =>
                                    <option key={'chain-status-' + index} value={status.id}>{status.description}</option>
                                )}
                            </FormControlWrapper>
                        </td>
                    )}

                    <td>
                        <FormControlWrapper
                            componentClass="select"
                            value={swap.clusterStatusId}
                            style={{ width: "initial" }}
                            onChange={this.handleClusterStatusChange}
                        >
                            {clusterStatusList.map((status, index) =>
                                <option key={index} value={status.id}>{status.description}</option>
                            )}
                        </FormControlWrapper>
                    </td>
                    <td>
                        {this.props.clusterStatusId != ClusterStatus.Stalled ?
                            <Button bsSize="xs" onClick={this.handleShowActions}>
                                <Glyphicon glyph="cog" />
                                <span style={{ marginLeft: 3 }}>Action</span>
                            </Button>
                            :
                            <Label>Actions not allowed in stalled tab</Label>
                        }
                    </td>
                    <td>
                        {this.renderRange()}
                    </td>
                    <td>
                        <FormControlWrapper
                            componentClass="select"
                            value={swap.swapManager}
                            style={{ width: "initial" }}
                            onChange={this.handleSwapManagerChange}
                        >
                            <option value="NKR Logistics">- NKR Logistics -</option>
                            {swapManagers.map((item, index) =>
                                <option key={'swap-manager-' + index} value={item.fullName}>{item.fullName}</option>
                            )}
                        </FormControlWrapper>
                    </td>
                    <td>
                        {this.props.clusterStatusId == ClusterStatus.Offers || this.props.clusterStatusId == ClusterStatus.Stalled || this.props.clusterStatusId == ClusterStatus.CrossMatching || this.props.clusterStatusId == ClusterStatus.Logistics ?
                            <FormControlWrapper
                                componentClass="select"
                                value={swap.courierVendor}
                                style={{ width: "initial" }}
                                onChange={this.handleLogisticsProviderChange}
                            >
                                {this.props.logisticsProviders.filter(item => item.isActive).map((item, index) =>
                                    <option key={'logistics-provider-' + index} value={item.id}>{item.description}</option>
                                )}
                            </FormControlWrapper>
                            :
                            <div>
                                {logisticsProviderDescription}
                            </div>
                        }
                    </td>

                    {(this.props.clusterStatusId == ClusterStatus.Offers || this.props.clusterStatusId == ClusterStatus.CrossMatching) && (
                        <td style={{ color: "#333" }}>
                            {this.renderClusterDeadline()}
                        </td>
                    )}

                    {this.props.clusterStatusId == ClusterStatus.Kickoff && (
                        <td style={{ color: "#333" }}>
                            <DateTimePicker
                                property={DateProperties.KICKOFF_DATETIME}
                                currentValue={swap.kickoffDateTime}
                                onValueChanged={this.handleDateTimeValueChange}
                            />
                        </td>
                    )}

                    {this.props.clusterStatusId == ClusterStatus.Logistics && (
                        <td style={{ color: "#333" }}>
                            <DateTimePicker
                                property={DateProperties.LOGISTICS_DATETIME}
                                currentValue={swap.logisticsDateTime}
                                onValueChanged={this.handleDateTimeValueChange}
                            />
                        </td>
                    )}

                    {this.props.clusterStatusId == ClusterStatus.Stalled && swap.positions.length > 0 && (
                        <td>
                            {swap.positions[0].donor.isActive ? "Yes" : "No"}
                        </td>
                    )}

                    <td onClick={this.handleEditComment}>
                        {this.renderNote()}
                    </td>
                </RowWrapper>

                {showDetail && (
                    <RowWrapper showHighlight={this.props.showDetail} padding="0px !important" useBackupPlan={false}>
                        <td colSpan={18} style={{ borderTop: "0px none", borderLeft: "1px solid #bbb", borderRight: "1px solid #bbb" }}>
                            <PositionList
                                chainId={swap.chainId}
                                clusterNumber={swap.clusterNumber}
                                clusterId={swap.id}
                                chainStatusId={this.props.chainStatusId}
                                clusterStatusId={this.props.clusterStatusId}
                                swapChainStatusId={swap.chainStatusId}
                                swapClusterStatusId={swap.clusterStatusId}
                                user={this.props.user}
                                showMap={this.handleShowMap}
                                onUpdateAfterReset={this.handleUpdateAfterReset}
                                onUpdateSwaps={this.props.onUpdateSwaps}
                            />
                        </td>
                    </RowWrapper>
                )}
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        clusterStatusList: state.swaps.clusterStatusList,
        chainStatusList: state.swaps.chainStatusList,
        staffList: state.swaps.staffList,
        logisticsProviders: state.swaps.logisticsProviders,
        pairStatusList: state.swaps.pairStatusList,
        centerList: state.swaps.centerList,
        transportTypeList: state.swaps.transportTypeList,
        swapTypeList: state.swaps.swapTypeList
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        refreshSwaps: () => {
            return dispatch(SwapActionCreators.refreshSwaps());
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(SwapListRow);